import * as React from 'react';
import dayjs from 'dayjs';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid2';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import useAxios from '../utils/useAxios';
import PageShell from './layout/PageShell';
import { ShellContext } from './layout/AppShell';
import { EmptyState, NumberField, SectionCard, currency } from './ui';
import FeeRuleEditor from './FeeRuleEditor';
import { FeeRule } from './business/cart';
import type { OrderDiscounts } from './business/OrderPage';
import { CatalogProduct, ClientOption, NewOrderDialog, OrderLinesDialog, totalMessage } from './OrderLines';
import OrdersProfit from './OrdersProfit';
import { moduleOn } from '../utils/features';

type OrderLine = {
  name: string; quantity: number; price: number; notes: string; options: { group_name: string; name: string }[];
  id?: number; product?: number | null;
  /** False while the price is an estimate (0: still to be priced). */
  confirmed?: boolean;
};
export type Order = {
  uuid: string; folio: string; status: 'open' | 'accepted' | 'ready' | 'paid' | 'canceled'; status_label: string;
  fulfillment: 'pickup' | 'delivery'; fulfillment_label: string; created_at: string; items: OrderLine[]; subtotal: number; fee: number;
  amount: number; notes: string; customer_name: string; phone: string; delivery_address: string;
  // Orders priced per customer (docs/ORDERS.md part 2).
  service_fee?: number; service_fee_label?: string; pending_prices?: number; photos?: { id: number; url: string }[]; track_path?: string;
  /** Where to deliver, when the customer shared their location. */
  delivery_location?: { lat: number; lng: number; accuracy: number | null; map_url: string } | null;
  /** Paid online when it was placed (the business's provider): delivered without charging again. */
  paid_online?: boolean;
  /** What the customer paid with their saldo when they ordered, and what's still to collect. */
  balance_used?: number; due?: number;
} & OrderDiscounts;

/** What's still to collect for an order when it's picked up or delivered. */
const dueOf = (order: Order) => order.due ?? (order.paid_online ? 0 : order.amount);
export type Settings = {
  enabled: boolean; pickup: boolean; delivery: boolean; delivery_fee: FeeRule; min_order: number; whatsapp: string; message: string;
  service_fee?: FeeRule; service_fee_label?: string; estimated_prices?: boolean; free_text?: boolean;
  pay_online?: boolean; pay_online_required?: 'never' | 'delivery' | 'always';
};

const errorText = (err: any, fallback: string) => {
  const data = err?.response?.data;
  if (typeof data === 'string') return data;
  if (typeof data?.detail === 'string') return data.detail;
  if (data && typeof data === 'object') return Object.values(data).flat().join(' ');
  return fallback;
};

const NEXT: Record<string, { status: string; label: string } | null> = {
  open: { status: 'accepted', label: 'Aceptar' },
  accepted: { status: 'ready', label: 'Marcar listo' },
  ready: null,
};
const STATUS_COLOR: Record<string, 'warning' | 'info' | 'success'> = { open: 'warning', accepted: 'info', ready: 'success' };

/** One order: who, how it's delivered, what they asked for, and the next step. */
export const OrderCard = ({ order, onMove, onCharge, onPrices, busy }: {
  order: Order; onMove: (order: Order, status: string) => void; onCharge: (order: Order) => void; busy: boolean;
  /** Confirm what each item cost and attach the purchase ticket. */
  onPrices?: (order: Order) => void;
}) => {
  const next = NEXT[order.status];
  const phoneDigits = order.phone.replace(/\D/g, '');
  const waNumber = phoneDigits.length === 10 ? `52${phoneDigits}` : phoneDigits;
  const extras = [order.fee ? `envío ${currency(order.fee)}` : '',
    order.service_fee ? `${(order.service_fee_label || 'Comisión por servicio').toLowerCase()} ${currency(order.service_fee)}` : ''].filter(Boolean);
  return (
    <Box data-testid="order-card" sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontWeight: 700 }}>{order.folio}</Typography>
        <Chip size="small" label={order.status_label} color={STATUS_COLOR[order.status] ?? 'default'} />
      </Stack>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>{dayjs(order.created_at).fromNow()} · {order.fulfillment_label}</Typography>
      <Typography sx={{ fontWeight: 600, mt: 1 }}>{order.customer_name}</Typography>
      {order.phone && (
        <Typography variant="body2">
          <a href={`tel:${phoneDigits}`}>{order.phone}</a>
          {' · '}<a href={`https://wa.me/${phoneDigits.length === 10 ? `52${phoneDigits}` : phoneDigits}`} target="_blank" rel="noopener noreferrer">WhatsApp</a>
        </Typography>
      )}
      {order.fulfillment === 'delivery' && order.delivery_address && <Typography variant="body2" sx={{ mt: 0.5 }}>📍 {order.delivery_address}</Typography>}
      {order.fulfillment === 'delivery' && order.delivery_location && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          <a href={order.delivery_location.map_url} target="_blank" rel="noopener noreferrer" data-testid="order-map">
            Ver su ubicación en el mapa{order.delivery_location.accuracy ? ` (± ${order.delivery_location.accuracy} m)` : ''}
          </a>
        </Typography>
      )}
      <Box component="ul" sx={{ pl: 2.5, my: 1 }}>
        {order.items.map((item, i) => (
          <li key={i}>
            <Typography variant="body2" component="div"><strong>{item.quantity} × {item.name}</strong>{item.options.length ? ` · ${item.options.map(o => o.name).join(', ')}` : ''}
              {item.confirmed === false
                ? <Chip size="small" variant="outlined" color="warning" sx={{ ml: 0.75, height: 20 }} label={item.price ? `${currency(item.price)} estimado` : 'Por cotizar'} />
                : order.service_fee ? <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}> · {currency(item.price * item.quantity)}</Typography> : null}
            </Typography>
            {item.notes && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{item.notes}</Typography>}
          </li>
        ))}
      </Box>
      {order.notes && <Alert severity="info" icon={false} sx={{ py: 0, mb: 1 }}>{order.notes}</Alert>}
      {!!order.pending_prices && (
        <Alert severity="warning" sx={{ py: 0, mb: 1 }} data-testid="pending-prices">
          {order.pending_prices === 1 ? 'Un precio' : `${order.pending_prices} precios`} por confirmar.
        </Alert>
      )}
      {((order.promotions ?? []).length > 0 || order.reward) && (
        <Box data-testid="order-discounts" sx={{ mt: 'auto', mb: 0.5 }}>
          {(order.promotions ?? []).map((promo, i) => (
            <Typography key={i} variant="body2" sx={{ color: 'success.main' }}>{promo.name} −{currency(promo.amount)}</Typography>
          ))}
          {order.reward && (
            <Typography variant="body2" sx={{ color: 'success.main' }}>
              Recompensa «{order.reward.title}»{order.reward.amount ? ` −${currency(order.reward.amount)}` : ''}
              {order.reward.gifts.length ? <strong> · incluye de regalo: {order.reward.gifts.join(', ')}</strong> : null}
            </Typography>
          )}
        </Box>
      )}
      <Typography sx={{ fontWeight: 700, mt: (order.promotions ?? []).length > 0 || order.reward ? 0 : 'auto' }}>
        {order.pending_prices ? 'Total aprox.' : 'Total'} {currency(order.amount)}
        {extras.length ? <Typography component="span" variant="body2" sx={{ color: 'text.secondary', fontWeight: 400 }}> ({extras.join(' · ')})</Typography> : null}
      </Typography>
      {!!order.balance_used && (
        <Typography variant="body2">Pagado con saldo {currency(order.balance_used)}{dueOf(order) > 0 ? ` · por cobrar ${currency(dueOf(order))}` : ''}</Typography>
      )}
      {(onPrices || (order.phone && !order.pending_prices)) && (
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
          {onPrices && <Button size="small" variant={order.pending_prices ? 'contained' : 'outlined'} color={order.pending_prices ? 'warning' : 'inherit'} disabled={busy}
            onClick={() => onPrices(order)} data-testid="order-prices">Precios{order.photos?.length ? ` · ${order.photos.length} 📷` : ''}</Button>}
          {order.phone && !order.pending_prices && (
            <Button size="small" variant="outlined" color="inherit" startIcon={<WhatsAppIcon />} target="_blank" rel="noopener noreferrer"
              href={`https://wa.me/${waNumber}?text=${encodeURIComponent(totalMessage(order))}`} data-testid="send-total">
              Enviar total
            </Button>
          )}
        </Stack>
      )}
      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        {next && <Button variant="contained" size="small" disabled={busy} onClick={() => onMove(order, next.status)}>{next.label}</Button>}
        <Button variant={next ? 'outlined' : 'contained'} size="small" disabled={busy} onClick={() => onCharge(order)}>{dueOf(order) <= 0 ? 'Entregar' : 'Cobrar'}</Button>
        <Button color="error" size="small" disabled={busy} onClick={() => onMove(order, 'canceled')} sx={{ ml: 'auto !important' }}>Cancelar</Button>
      </Stack>
    </Box>
  );
};

/** /admin/orders: orders sent from the catalog (to accept, prepare and charge) and how the business takes them. */
const Orders = () => {
  const axios = useAxios();
  const { membership, features } = React.useContext(ShellContext);
  const isAdmin = !!membership?.is_admin;
  const [tab, setTab] = React.useState<'orders' | 'profit' | 'settings'>('orders');
  const [orders, setOrders] = React.useState<Order[] | null>(null);
  const [settings, setSettings] = React.useState<Settings | null>(null);
  const [draft, setDraft] = React.useState<Settings | null>(null);
  const [slug, setSlug] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [charging, setCharging] = React.useState<Order | null>(null);
  const [error, setError] = React.useState('');
  const [notice, setNotice] = React.useState('');
  const [pricing, setPricing] = React.useState<Order | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [products, setProducts] = React.useState<CatalogProduct[]>([]);
  const [clients, setClients] = React.useState<ClientOption[]>([]);
  // Paying online needs an online payment provider connected (Mercado Pago or another, Configuración).
  const [mercadoPago, setMercadoPago] = React.useState<boolean | null>(null);
  // A business that charges a service fee buys what each customer asks for: it prices orders and sees its profit.
  const perCustomer = !!settings?.service_fee && settings.service_fee.type !== 'none';
  // The service-fee settings belong to businesses that buy per customer order (Configuración › Tu negocio).
  const showEncargos = perCustomer || !!settings?.estimated_prices || !!settings?.free_text || moduleOn(features, 'encargos');

  const load = React.useCallback(() => {
    axios.get('api/orders/').then(r => setOrders(r.data)).catch(() => setOrders(current => current ?? []));
  }, [axios]);
  React.useEffect(() => {
    load();
    const timer = window.setInterval(load, 15000);
    axios.get('api/orders/settings/').then(r => { setSettings(r.data); setDraft(r.data); }).catch(() => {});
    axios.get('management/organization/').then(r => setSlug(r.data?.slug ?? '')).catch(() => {});
    axios.get('api/integrations/providers/').then(r => setMercadoPago(!!r.data?.online?.provider))
      .catch(() => axios.get('api/integrations/mercadopago/').then(r => setMercadoPago(!!r.data?.connected)).catch(() => setMercadoPago(null)));
    return () => window.clearInterval(timer);
  }, [axios, load]);
  // The catalog and the customers, for adding lines and taking orders (loaded once, when first needed).
  const needsCatalog = creating || !!pricing;
  React.useEffect(() => {
    if (!needsCatalog || products.length) return;
    axios.get('api/products/').then(r => setProducts((r.data ?? []).filter((p: any) => p.enabled !== false)
      .map((p: any) => ({ id: p.id, name: p.name, price: Number(p.price) || 0 })))).catch(() => {});
  }, [axios, needsCatalog, products.length]);
  React.useEffect(() => {
    if (!creating || clients.length) return;
    axios.get('api/clients/').then(r => setClients(r.data ?? [])).catch(() => {});
  }, [axios, creating, clients.length]);

  const move = async (order: Order, status: string) => {
    if (status === 'canceled' && !window.confirm(`¿Cancelar el pedido ${order.folio} de ${order.customer_name}?`)) return;
    setBusy(true);
    setError('');
    try {
      await axios.post(`api/orders/${order.uuid}/status/`, { status });
      load();
    } catch (err) {
      setError(errorText(err, 'No se pudo cambiar el pedido.'));
    } finally {
      setBusy(false);
    }
  };
  const charge = async (payment: string) => {
    if (!charging) return;
    setBusy(true);
    setError('');
    try {
      await axios.post(`api/orders/${charging.uuid}/charge/`, { payment });
      setNotice(dueOf(charging) <= 0 ? `Pedido ${charging.folio} entregado: ya es una venta.` : `Pedido ${charging.folio} cobrado: ya es una venta.`);
      setCharging(null);
      load();
    } catch (err) {
      setError(errorText(err, 'No se pudo cobrar el pedido.'));
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    if (!draft) return;
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.put('api/orders/settings/', draft);
      setSettings(data);
      setDraft(data);
      setNotice(data.enabled ? 'Guardado. Tu catálogo ya recibe pedidos.' : 'Guardado. Tu catálogo no recibe pedidos.');
    } catch (err) {
      setError(errorText(err, 'No se pudo guardar.'));
    } finally {
      setBusy(false);
    }
  };
  const set = <K extends keyof Settings>(key: K, value: Settings[K]) => setDraft(d => (d ? { ...d, [key]: value } : d));
  const changed = JSON.stringify(draft) !== JSON.stringify(settings);

  return (
    <PageShell
      title="Pedidos"
      subtitle="Lo que tus clientes piden desde tu catálogo en línea: acéptalo, prepáralo y cóbralo al entregarlo."
      actions={(
        <Stack direction="row" spacing={1}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)} data-testid="new-order">Nuevo pedido</Button>
          {slug && <Button color="inherit" endIcon={<OpenInNewIcon />} href={`/${slug}`} target="_blank" rel="noopener">Ver mi catálogo</Button>}
        </Stack>
      )}
      toolbar={(
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile>
          <Tab value="orders" label={`Por atender${orders ? ` (${orders.length})` : ''}`} />
          {perCustomer && <Tab value="profit" label="Ganancia y gastos" />}
          <Tab value="settings" label="Configuración" />
        </Tabs>
      )}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

      {tab === 'orders' && (
        orders && orders.length === 0 ? (
          <EmptyState icon={<ShoppingBagOutlinedIcon />} title="No hay pedidos por atender"
            description={settings?.enabled ? 'Los nuevos aparecen aquí y en la caja.' : 'Activa los pedidos en línea para que tus clientes pidan desde tu catálogo.'}
            action={settings?.enabled ? undefined : <Button variant="contained" onClick={() => setTab('settings')}>Configurar</Button>} />
        ) : (
          <Grid container spacing={2}>
            {(orders ?? []).map(order => (
              <Grid key={order.uuid} size={{ xs: 12, md: 6, lg: 4 }}>
                <OrderCard order={order} busy={busy} onMove={move} onCharge={setCharging}
                  onPrices={perCustomer || order.pending_prices || order.items.some(i => i.product == null) ? setPricing : undefined} />
              </Grid>
            ))}
          </Grid>
        )
      )}

      {tab === 'profit' && <OrdersProfit />}

      {tab === 'settings' && draft && (
        <Stack spacing={3} sx={{ maxWidth: 760 }}>
          {!isAdmin && <Alert severity="info">Solo un administrador puede cambiar esta configuración.</Alert>}
          <SectionCard title="Pedidos en línea" subtitle="Tus clientes arman su pedido en tu catálogo y te llega aquí y a la caja.">
            <Stack spacing={1}>
              <FormControlLabel label="Recibir pedidos desde mi catálogo" control={<Switch checked={draft.enabled} disabled={!isAdmin} onChange={e => set('enabled', e.target.checked)} />} />
              <FormControlLabel label="Para recoger en tienda" control={<Switch checked={draft.pickup} disabled={!isAdmin} onChange={e => set('pickup', e.target.checked)} />} />
              <FormControlLabel label="A domicilio" control={<Switch checked={draft.delivery} disabled={!isAdmin} onChange={e => set('delivery', e.target.checked)} />} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pt: 1 }}>
                <NumberField label="Pedido mínimo ($)" value={draft.min_order} disabled={!isAdmin} onChange={v => set('min_order', v ?? 0)} helperText="0 = sin mínimo" />
                <TextField label="WhatsApp del negocio" value={draft.whatsapp} disabled={!isAdmin} onChange={e => set('whatsapp', e.target.value)}
                  helperText="Para que tus clientes te escriban sobre su pedido." />
              </Stack>
              <TextField label="Mensaje para tus clientes (opcional)" value={draft.message} disabled={!isAdmin} onChange={e => set('message', e.target.value)}
                helperText="Aparece al enviar el pedido, p. ej. horario de entregas." slotProps={{ htmlInput: { maxLength: 200 } }} />
            </Stack>
          </SectionCard>
          {draft.delivery && (
            <SectionCard title="Costo de envío" subtitle="Cómo se cobra el envío a domicilio, según el total del pedido.">
              <FeeRuleEditor rule={draft.delivery_fee} disabled={!isAdmin} noun="envío" onChange={rule => set('delivery_fee', rule)} />
            </SectionCard>
          )}
          <SectionCard title="Pago en línea" subtitle="Tus clientes pagan al hacer su pedido; el dinero llega a la cuenta de tu proveedor de pagos (Mercado Pago u otro que conectes en Configuración).">
            <Stack spacing={1.5}>
              {mercadoPago === false && (
                <Alert severity="info">Para cobrar en línea, conecta tu cuenta en <a href="/admin/settings">Configuración › Cobros con tarjeta</a>.</Alert>
              )}
              {!!draft.estimated_prices && draft.pay_online && (
                <Alert severity="warning">Con precios aproximados el total cambia después del pedido: tus clientes pagan al recibirlo.</Alert>
              )}
              <FormControlLabel disabled={!isAdmin} control={<Switch checked={!!draft.pay_online} onChange={e => set('pay_online', e.target.checked)} data-testid="pay-online-switch" />}
                label={<Box><Typography>Mis clientes pueden pagar en línea</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>El pedido te llega cuando se aprueba su pago y en caja se entrega sin cobrar otra vez. Si lo cancelas, se le devuelve el dinero.</Typography></Box>} />
              {draft.pay_online && (
                <TextField select label="Pago en línea obligatorio" value={draft.pay_online_required ?? 'never'} disabled={!isAdmin} sx={{ maxWidth: 360 }}
                  onChange={e => set('pay_online_required', e.target.value as Settings['pay_online_required'])}>
                  <MenuItem value="never">No: pueden pagar al recoger o al recibir</MenuItem>
                  <MenuItem value="delivery">Solo en pedidos a domicilio</MenuItem>
                  <MenuItem value="always">En todos los pedidos</MenuItem>
                </TextField>
              )}
            </Stack>
          </SectionCard>
          {showEncargos && (
            <SectionCard title="Comisión por servicio"
              subtitle="Si compras lo que cada cliente te pide (en Costco, en el mercado…): cada pedido paga lo que costaron sus productos más tu comisión, según el total de ese pedido.">
              <Stack spacing={2}>
                <FeeRuleEditor rule={draft.service_fee ?? { type: 'none' }} disabled={!isAdmin} noun="comisión" examples={[500, 1250, 3500]}
                  onChange={rule => set('service_fee', rule)} />
                {draft.service_fee && draft.service_fee.type !== 'none' && (
                  <TextField label="Cómo se llama en el ticket" value={draft.service_fee_label ?? 'Comisión por servicio'} disabled={!isAdmin}
                    onChange={e => set('service_fee_label', e.target.value)} slotProps={{ htmlInput: { maxLength: 40 } }} sx={{ maxWidth: 360 }} />
                )}
                <FormControlLabel disabled={!isAdmin} control={<Switch checked={!!draft.estimated_prices} onChange={e => set('estimated_prices', e.target.checked)} />}
                  label={<Box><Typography>Los precios de mi catálogo son aproximados</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Confirmas lo que costó cada producto al comprarlo; tu cliente ve su total aproximado hasta entonces.</Typography></Box>} />
                <FormControlLabel disabled={!isAdmin} control={<Switch checked={!!draft.free_text} onChange={e => set('free_text', e.target.checked)} />}
                  label={<Box><Typography>Mis clientes pueden pedir lo que no está en mi catálogo</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Lo escriben en su pedido y tú le pones precio al comprarlo.</Typography></Box>} />
              </Stack>
            </SectionCard>
          )}
          {isAdmin && (
            <Box><Button variant="contained" onClick={save} disabled={!changed || busy}>{busy ? 'Guardando…' : 'Guardar'}</Button></Box>
          )}
        </Stack>
      )}

      {pricing && (
        <OrderLinesDialog order={pricing} settings={settings} products={products} isAdmin={isAdmin} onClose={() => setPricing(null)}
          onSaved={saved => { setPricing(null); setNotice(`Precios del pedido ${saved.folio} guardados: total ${currency(saved.amount)}.`); load(); }} />
      )}
      {creating && (
        <NewOrderDialog settings={settings} products={products} clients={clients} isAdmin={isAdmin} onClose={() => setCreating(false)}
          onCreated={created => { setCreating(false); setTab('orders'); setNotice(`Pedido ${created.folio} de ${created.customer_name} creado.`); load(); }} />
      )}

      <Dialog open={!!charging} onClose={() => !busy && setCharging(null)}>
        <DialogTitle>{charging && dueOf(charging) <= 0 ? 'Entregar' : 'Cobrar'} pedido {charging?.folio}</DialogTitle>
        <DialogContent>
          <Typography>{charging?.customer_name} · Total <strong>{currency(charging?.amount ?? 0)}</strong></Typography>
          {!!charging?.balance_used && (
            <Typography>Pagado con saldo {currency(charging.balance_used)}{dueOf(charging) > 0 ? <> · por cobrar <strong>{currency(dueOf(charging))}</strong></> : ''}</Typography>
          )}
          {!!charging?.pending_prices && <Alert severity="warning" sx={{ mt: 1 }}>Hay precios sin confirmar: el total puede cambiar. Confírmalos antes de cobrar.</Alert>}
          {charging && dueOf(charging) <= 0
            ? <Alert severity="success" sx={{ mt: 1 }}>{charging.paid_online ? 'Ya está pagado en línea' : 'Ya está pagado con su saldo'}: entrégalo sin cobrar.</Alert>
            : <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>¿Cómo pagó?</Typography>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setCharging(null)} disabled={busy}>Cancelar</Button>
          {charging && dueOf(charging) <= 0
            ? <Button variant="contained" disabled={busy} onClick={() => charge(charging.paid_online ? 'online' : 'balance')}>Entregar</Button>
            : [['cash', 'Efectivo'], ['card', 'Tarjeta'], ['transfer', 'Transferencia']].map(([value, label]) => (
              <Button key={value} variant="contained" disabled={busy} onClick={() => charge(value)}>{label}</Button>
            ))}
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default Orders;
